import { useEffect, useRef, useState } from "react";
import { ShallyPlanCard } from "@/components/outbound/ShallyPlanCard";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mic, PhoneOff } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

const OVERLAP_MS = 700;

function Usage({ u }) {
  if (!u) return null;
  return (
    <div className="grid grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm sm:grid-cols-4" data-testid="sl-usage">
      <div><p className="text-xs text-slate-500">Voice used</p><p className="font-semibold" data-testid="sl-used">{u.used_seconds}s of {u.budget_seconds}s</p></div>
      <div><p className="text-xs text-slate-500">Remaining</p><p className="font-semibold" data-testid="sl-remaining">{u.remaining_seconds}s</p></div>
      <div><p className="text-xs text-slate-500">Estimated voice cost</p><p className="font-semibold" data-testid="sl-cost">${u.estimated_cost_usd.toFixed(3)}</p></div>
      <div><p className="text-xs text-slate-500">Model / voice</p><p className="font-semibold">{u.model} / {u.voice}</p></div>
      <p className="col-span-full text-xs text-slate-500">${u.rate_per_minute_usd}/min billed per second; each session start reserves 15 s. Business lookups use Pawkt's existing AI (not OpenAI).</p>
    </div>);
}

function Transcript({ lines }) {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [lines]);
  return (
    <div className="h-72 space-y-1 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="sl-transcript">
      {lines.map((l, i) => (
        <p key={i} className={l.who === "you" ? "text-slate-900" : l.who === "shally" ? "text-violet-800" : "text-xs text-amber-700"}>
          <span className="font-semibold">{l.who === "you" ? "You" : l.who === "shally" ? "Shally" : "•"}</span> {l.text}</p>))}
      <div ref={end} />
    </div>);
}

function Stats({ s }) {
  const med = (a) => (a.length ? [...a].sort((x, y) => x - y)[Math.floor(a.length / 2)] : "–");
  return (
    <div className="flex flex-wrap gap-4 text-xs text-slate-600" data-testid="sl-stats">
      <span>Interruptions detected: <b data-testid="sl-interruptions">{s.interruptions}</b></span>
      <span>Lookups: <b>{s.asks}</b></span>
      <span>Lookup time (median): <b>{med(s.askMs)} ms</b></span>
      <span>Caller stops → Shally speaks (median): <b data-testid="sl-latency">{med(s.replyMs)} ms</b></span>
      <span>Session setup: <b>{s.createMs ?? "–"} ms</b></span>
    </div>);
}

const blankStats = () => ({ interruptions: 0, asks: 0, askMs: [], replyMs: [], createMs: null });

export default function ShallyLive() {
  const qc = useQueryClient();
  const { data: u } = useQuery({ queryKey: ["shally-live-usage"], queryFn: async () => (await api.get("/shally-live/usage")).data });
  const { data: branches = [] } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data, staleTime: 300e3 });
  const [branch, setBranch] = useState("");
  const [status, setStatus] = useState("idle");
  const [lines, setLines] = useState([]);
  const [stats, setStats] = useState(blankStats());
  const r = useRef({});

  const add = (who, text) => setLines((ls) => {
    const last = ls[ls.length - 1];
    if (who !== "note" && last && last.who === who && !last.closed) return [...ls.slice(0, -1), { ...last, text: last.text + text }];
    return [...ls, { who, text }];
  });
  const bump = (k, v) => setStats((s) => (Array.isArray(s[k]) ? { ...s, [k]: [...s[k], v] } : { ...s, [k]: v ?? s[k] + 1 }));
  const send = (ev) => r.current.dc?.readyState === "open" && r.current.dc.send(JSON.stringify(ev));

  const report = async (final) => {
    const c = r.current;
    if (!c.sid) return;
    try { await api.post(`/shally-live/${c.sid}/usage`, { seconds: c.seconds || 0, final, stats: final ? { interruptions: c.interruptions || 0 } : {} }); } catch { /* usage retried at end */ }
    qc.invalidateQueries({ queryKey: ["shally-live-usage"] });
  };
  const cleanup = async (msg) => {
    const c = r.current;
    clearTimeout(c.cap); clearTimeout(c.closeT);
    c.mic?.getTracks().forEach((t) => t.stop()); c.dc?.close(); c.pc?.close();
    if (c.audio) c.audio.srcObject = null;
    await report(true);
    r.current = {}; setStatus("idle"); if (msg) add("note", msg);
  };

  const onDelegation = async (id) => {
    const c = r.current;
    const q = (c.userBuf || "").trim() || c.lastUser || "";
    c.lastUser = q || c.lastUser; c.userBuf = "";
    add("note", `Looking up: "${q.slice(0, 80)}"`);
    try {
      const { data } = await api.post(`/shally-live/${c.sid}/ask`, { question: q || "(no transcript)", delegation_id: id });
      bump("asks"); bump("askMs", data.ms);
      send({ type: "session.commentary.append", event_id: `res_${id}`.slice(0, 60), delegation_id: id, content: `Verified business result: ${data.answer}` });
      add("note", `Pawkt result (${data.intent}, ${data.ms} ms): ${data.answer}`);
    } catch (e) {
      send({ type: "session.commentary.append", delegation_id: id, content: "The business lookup failed. Tell the caller the team will confirm." });
      add("note", `Lookup failed: ${apiErr(e)}`);
    }
  };

  const onEvent = (ev) => {
    const c = r.current, t = performance.now();
    if (ev.type === "session.started") { setStatus("live"); add("note", "Connected. Start talking."); }
    else if (ev.type === "session.input_transcript.delta") {
      if (c.lastOut && t - c.lastOut < OVERLAP_MS && !c.overlap) { c.overlap = true; c.interruptions = (c.interruptions || 0) + 1; bump("interruptions"); add("note", "You spoke while Shally was speaking (interruption)"); }
      c.userBuf = (c.userBuf || "") + ev.delta; c.lastIn = t; c.awaitReply = true; add("you", ev.delta);
    } else if (ev.type === "session.output_transcript.delta") {
      if (c.awaitReply && c.lastIn) { bump("replyMs", Math.round(t - c.lastIn)); c.awaitReply = false; }
      c.lastOut = t; c.overlap = false; add("shally", ev.delta);
    } else if (ev.type === "session.delegation.created") onDelegation(ev.delegation.id);
    else if (ev.type === "session.usage.updated") { c.seconds = ev.usage?.seconds || c.seconds; if (c.seconds >= c.max - 3) stop("Budget limit reached — ending session."); }
    else if (ev.type === "session.closed") { c.seconds = ev.usage?.seconds ?? c.seconds; c.final = true; cleanup(`Session closed. Final voice usage: ${c.seconds}s.`); }
    else if (ev.type === "error") add("note", `Error: ${ev.error?.message || ev.error?.code || "unknown"}`);
  };

  const start = async () => {
    setStatus("connecting"); setLines([]); setStats(blankStats());
    const c = (r.current = {});
    try {
      c.pc = new RTCPeerConnection();
      c.audio = new Audio(); c.audio.autoplay = true;
      c.pc.addEventListener("track", (e) => { c.audio.srcObject = new MediaStream([e.track]); c.audio.play().catch(() => {}); });
      c.mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      c.mic.getAudioTracks().forEach((tr) => c.pc.addTrack(tr, c.mic));
      c.dc = c.pc.createDataChannel("oai-events");
      c.dc.addEventListener("message", ({ data }) => onEvent(JSON.parse(data)));
      await c.pc.setLocalDescription(await c.pc.createOffer());
      if (c.pc.iceGatheringState !== "complete") {
        await new Promise((res) => { const f = () => c.pc.iceGatheringState === "complete" && res(); c.pc.addEventListener("icegatheringstatechange", f); setTimeout(res, 5000); });
      }
      const { data } = await api.post("/shally-live/session", { sdp: c.pc.localDescription.sdp, branch_id: branch });
      c.sid = data.id; c.max = data.max_seconds; bump("createMs", data.create_ms);
      await c.pc.setRemoteDescription({ type: "answer", sdp: data.sdp });
      c.cap = setTimeout(() => stop("Budget limit reached — ending session."), Math.max(5, data.max_seconds - 3) * 1000);
    } catch (e) { await cleanup(`Could not start: ${apiErr(e) || e.message}`); }
  };
  const stop = (msg) => {
    const c = r.current;
    if (!c.dc) return;
    if (msg) add("note", msg);
    setStatus("closing");
    send({ type: "session.close" });
    c.closeT = setTimeout(() => cleanup("No final usage event received; recorded the last known usage."), 12000);
  };
  useEffect(() => () => { if (r.current.pc) stop(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const live = status === "live" || status === "connecting" || status === "closing";
  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="sl-page">
      <div>
        <Link to="/settings/ai-receptionist" className="text-sm text-violet-700 underline" data-testid="sl-back">AI Receptionist</Link>
        <h1 className="mt-1 text-2xl font-bold">Talk to Shally (Live voice prototype)</h1>
        <p className="text-sm text-slate-600">Preview test only. Uses your microphone and OpenAI GPT-Live. Business answers come from your Pawkt records. No bookings are created and no phone call is made. Use headphones for the cleanest interruption test.</p>
      </div>
      <ShallyPlanCard />
      <Usage u={u} />
      {u && !u.configured && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="sl-no-key">Add your OpenAI API key in Secrets to enable this test.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={branch} onChange={(e) => setBranch(e.target.value)} disabled={live} data-testid="sl-branch">
          <option value="">All branches</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        {!live ? <Button onClick={start} disabled={!u?.configured || !u?.entitled || u.remaining_seconds < 20} data-testid="sl-start"><Mic className="mr-1 h-4 w-4" />Start conversation</Button>
          : <Button variant="destructive" onClick={() => stop()} disabled={status !== "live"} data-testid="sl-stop"><PhoneOff className="mr-1 h-4 w-4" />End conversation</Button>}
        <span className="text-sm text-slate-600" data-testid="sl-status">Status: {status}</span>
      </div>
      <Stats s={stats} />
      <Transcript lines={lines} />
    </div>);
}
